import PageBackTitle from "../../Component/PageBackTitle";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import { Settings2 } from "lucide-react";
import { hrRecordsApi } from "../../Services/hrRecordsApi";
import { formatDateOnlyIST, getDuePillStyle } from "../hrRecordsHelpers";
import "../hrRecords.css";
import "../../tables/User.css";

function MedicalCompliancePage({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [dueList, setDueList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        const [dashboardResponse, dueResponse] = await Promise.all([
          hrRecordsApi.getMedicalComplianceDashboard(),
          hrRecordsApi.listMedicalDue({ within_days: 90, include_overdue: true }),
        ]);

        setDashboard(dashboardResponse);
        setDueList(dueResponse.items || []);
      } catch (error) {
        console.error("Failed to load medical compliance:", error);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, []);

  return (
    <div
      className={`data-list-content-container page-content ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="data-list-content">
        <div className="data-list-header">
          <div className="data-list-header-content">
            <PageBackTitle>Medical Compliance</PageBackTitle>
            <div className="data-list-heading-content-right-side">
              <Tooltip title="Reminder Settings" arrow>
                <button
                  type="button"
                  className="new-button hr-records-settings-button"
                  onClick={() => navigate("/hr-medical-reminder-settings")}
                >
                  <Settings2 size={15} /> Settings
                </button>
              </Tooltip>
            </div>
          </div>
        </div>
        <div className="header-separator"></div>

        <div className="data-list-body">
          <div className="data-list-scroll-body hr-records-dashboard-scroll">
            {loading ? (
              <div className="loader-container">
                <div className="loader"></div>
                <div>Loading...</div>
              </div>
            ) : (
              <>
                {dashboard && (
                  <>
                    <div className="hr-records-cards">
                      {[
                        ["Employees Covered", dashboard.totals?.employees_covered],
                        ["Active", dashboard.totals?.active],
                        ["Expiring Soon", dashboard.totals?.expiring_soon],
                        ["Expired", dashboard.totals?.expired],
                        ["Unfit", dashboard.totals?.unfit],
                        ["Overdue", dashboard.totals?.overdue],
                      ].map(([label, value]) => (
                        <div className="hr-records-card" key={label}>
                          <div className="hr-records-card-label">{label}</div>
                          <div className="hr-records-card-value">{value || 0}</div>
                        </div>
                      ))}
                    </div>

                    <div className="hr-records-section">
                      <div className="hr-records-section-title">
                        By Medical Type
                      </div>
                      <div className="data-list-table">
                        {dashboard.by_type?.length ? (
                          <table className="table multilevel-data">
                            <thead>
                              <tr>
                                <th>MEDICAL TYPE</th>
                                <th>TOTAL</th>
                                <th>EMPLOYEES</th>
                                <th>EXPIRING</th>
                                <th>EXPIRED</th>
                                <th>EARLIEST DUE</th>
                              </tr>
                            </thead>
                            <tbody className="data-list-scroll-bodys">
                              {dashboard.by_type.map((item) => (
                                <tr key={item.medical_type} className="hoverable-row">
                                  <td>{item.medical_type || "NA"}</td>
                                  <td>{item.total || 0}</td>
                                  <td>{item.employees || 0}</td>
                                  <td>{item.expiring_soon || 0}</td>
                                  <td>{item.expired || 0}</td>
                                  <td>{formatDateOnlyIST(item.earliest_due)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <div className="hr-records-empty">No data found.</div>
                        )}
                      </div>
                    </div>
                  </>
                )}

                <div className="hr-records-section">
                  <div className="hr-records-section-title">Due in 90 Days</div>
                  <div className="data-list-table">
                    {dueList.length ? (
                      <table className="table multilevel-data">
                        <thead>
                          <tr>
                            <th>EMPLOYEE</th>
                            <th>MEDICAL TYPE</th>
                            <th>EXAM DATE</th>
                            <th>VALID TO</th>
                            <th>NEXT DUE</th>
                            <th>STATUS</th>
                          </tr>
                        </thead>
                        <tbody className="data-list-scroll-bodys">
                          {dueList.map((item) => (
                            <tr key={item.report_id} className="hoverable-row">
                              <td>{item.person_name || "NA"}</td>
                              <td>{item.medical_type || "NA"}</td>
                              <td>{formatDateOnlyIST(item.examination_date)}</td>
                              <td>{formatDateOnlyIST(item.valid_to)}</td>
                              <td>{formatDateOnlyIST(item.next_due_date)}</td>
                              <td>
                                <span
                                  className="hr-records-pill"
                                  style={getDuePillStyle(item.status)}
                                >
                                  {(item.status || "NA").replace(/_/g, " ")}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="hr-records-empty">No due records found.</div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MedicalCompliancePage;
